/**
 * UnifiedPayPage — صفحة الدفع الموحدة للمنصة بأكملها
 *
 * هذه الصفحة هي الوجهة الوحيدة لأي زر "ادفع الآن" في المنصة:
 *   • شراء منتج من المتجر الإلكتروني
 *   • شراء خدمة (visa, bill payment, …)
 *   • أي اشتراك أو عملية دفع مستقبلية
 *
 * تستقبل المعاملات التالية عبر query string:
 *   ?amount=<number>             المبلغ بالدرهم (مطلوب)
 *   &description=<string>        وصف العملية (مطلوب)
 *   &reference=<string>          رقم الطلب / الفاتورة للمطابقة (مطلوب)
 *   &currency=AED                اختياري، الافتراضي AED
 *   &return_to=<path>            المسار الذي نعود له عند الإلغاء
 *
 * عند الاختيار: تستدعي POST /api/customer/payment/create-link
 * ثم تحوّل المستخدم مباشرةً إلى checkout_url عند بوابة الدفع.
 */
import { useMemo, useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, Loader2, ShieldCheck, Receipt } from 'lucide-react';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import PaymentMethodPicker from '../components/PaymentMethodPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UnifiedPayPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';

  const amount = Number(params.get('amount') || 0);
  const description = params.get('description') || '';
  const reference = params.get('reference') || '';
  const currency = (params.get('currency') || 'AED').toUpperCase();
  const returnTo = params.get('return_to') || '/';

  const token = useMemo(() => localStorage.getItem('token'), []);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error(isAr ? 'يرجى تسجيل الدخول أولاً' : 'Please login first');
      navigate('/login', { state: { from: window.location.pathname + window.location.search } });
    }
  }, [token, navigate, isAr]);

  const paramsValid = amount > 0 && description && reference;

  const handleSelect = async (provider) => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/customer/payment/create-link`,
        { provider, amount, currency, description, reference },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const url = res.data?.checkout_url;
      if (!url) throw new Error(isAr ? 'لم يصل رابط الدفع' : 'No checkout url returned');
      try {
        localStorage.setItem('last_payment_reference', reference);
        const adId = params.get('ad');
        if (adId) localStorage.setItem('pending_ad_payment', JSON.stringify({ ad_id: adId, reference }));
      } catch (_) {}
      window.location.href = url;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(
        (typeof d === 'string' ? d : d?.message_ar || d?.message_en) ||
        e.message ||
        (isAr ? 'تعذّر بدء الدفع' : 'Failed to start payment'),
      );
      setSubmitting(false);
    }
  };

  if (!paramsValid) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-6 ${isDarkMode ? 'bg-[#0a0a0a] text-white' : 'bg-[#F8FAFC] text-[#0F172A]'}`} dir={isAr ? 'rtl' : 'ltr'}>
        <div className="max-w-md w-full bg-white dark:bg-[#141414] rounded-2xl p-6 border border-red-200 text-center space-y-3">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <Receipt className="w-7 h-7 text-red-500" />
          </div>
          <h1 className="text-lg font-bold">{isAr ? 'بيانات الدفع ناقصة' : 'Incomplete payment data'}</h1>
          <p className="text-sm text-[#64748B]">
            {isAr
              ? 'الرابط لا يحتوي على المبلغ أو رقم المرجع. ارجع إلى الصفحة السابقة وحاول مرة أخرى.'
              : 'The URL is missing amount, description or reference. Please return to the previous page.'}
          </p>
          <Link to={returnTo} className="inline-block mt-2 px-5 py-2 rounded-xl bg-[#1E9BD9] text-white font-medium">
            {isAr ? 'عودة' : 'Go back'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F8FAFC]'}`} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className={`sticky top-0 z-40 border-b ${isDarkMode ? 'bg-[#0a0a0a]/90 border-white/10' : 'bg-white/90 border-gray-200'} backdrop-blur-md`}>
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(returnTo)}
            data-testid="unified-pay-back"
            className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
          >
            <ChevronLeft className={`h-5 w-5 ${isAr ? 'rotate-180' : ''}`} />
          </button>
          <h1 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
            {isAr ? 'إتمام الدفع' : 'Complete Payment'}
          </h1>
          <div className="w-9" />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        {/* Amount summary */}
        <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white'} shadow-lg`} data-testid="unified-pay-summary">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>{isAr ? 'الوصف' : 'Description'}</div>
              <div className={`font-bold truncate ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{description}</div>
              <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`} dir="ltr">
                {isAr ? 'المرجع:' : 'Ref:'} {reference}
              </div>
            </div>
            <div className="text-end">
              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>{isAr ? 'المبلغ' : 'Amount'}</div>
              <div className="text-3xl font-bold text-[#1E9BD9] whitespace-nowrap">
                {amount.toFixed(2)} <span className="text-sm">{currency}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Methods */}
        <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white'} shadow-lg`}>
          <h2 className={`text-base font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
            {isAr ? 'اختر طريقة الدفع' : 'Choose a payment method'}
          </h2>
          <PaymentMethodPicker
            amount={amount}
            currency={currency}
            isAr={isAr}
            disabled={submitting}
            onSelect={handleSelect}
          />
          {submitting && (
            <div className="flex items-center justify-center mt-4 gap-2 text-[#64748B] text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              {isAr ? 'جارِ تحويلك إلى بوابة الدفع…' : 'Redirecting to payment gateway…'}
            </div>
          )}
        </div>

        {/* Security note */}
        <div className={`rounded-xl p-4 flex gap-3 ${isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-emerald-50 border border-emerald-200'}`}>
          <ShieldCheck className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-emerald-800'}`}>
            {isAr
              ? 'الدفع يتم عبر بوابات موثّقة (Stripe، تمارا، تابي، زينة) ولا تمر معلومات بطاقتك على خوادمنا أبداً.'
              : 'Payment is handled by trusted gateways (Stripe, Tamara, Tabby, Ziina). Your card details never touch our servers.'}
          </div>
        </div>
      </div>
    </div>
  );
}
